import { useCallback, useEffect, useState } from 'react';
import { api, hasToken } from './api';
import { BulkBar, Toast } from './components/BulkBar';
import { Header } from './components/Header';
import { Login } from './components/Login';
import { Sidebar } from './components/Sidebar';
import { Alerts } from './screens/Alerts';
import { Commands } from './screens/Commands';
import { Dashboard } from './screens/Dashboard';
import { Detail } from './screens/Detail';
import { Files } from './screens/Files';
import { Fleet } from './screens/Fleet';
import { Net } from './screens/Net';
import { Rdp } from './screens/Rdp';
import { Tasks } from './screens/Tasks';
import { AppProvider, storedTheme, useApp } from './store';
import { themeStyle } from './themes';

export function App() {
  // null — ещё выясняем у сервера, нужен ли вход.
  const [needsLogin, setNeedsLogin] = useState<boolean | null>(null);

  const check = useCallback(() => {
    api
      .session()
      .then(s => setNeedsLogin(s.authRequired && !hasToken()))
      .catch(() => setNeedsLogin(false));
  }, []);

  useEffect(check, [check]);

  const onAuthLost = useCallback(() => setNeedsLogin(true), []);

  if (needsLogin === null) return <Splash />;
  if (needsLogin) return <Login onDone={() => setNeedsLogin(false)} />;

  return (
    <AppProvider onAuthLost={onAuthLost}>
      <Shell />
    </AppProvider>
  );
}

function Shell() {
  const app = useApp();

  // Горячие клавиши: цифры — экраны, Esc — снять выбор.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'Escape') app.clearSel();
      const screens = ['dash', 'fleet', 'commands', 'files', 'rdp', 'net', 'tasks', 'alerts'] as const;
      const idx = Number(e.key) - 1;
      if (!e.metaKey && !e.ctrlKey && !e.altKey && idx >= 0 && idx < screens.length) app.go(screens[idx]);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [app]);

  return (
    <div style={themeStyle(app.theme, app.dense)}>
      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          minHeight: '100vh',
          background: 'var(--bg)',
          color: 'var(--ink)',
          fontFamily: 'var(--fs)',
          fontSize: 13.5,
          lineHeight: 1.5
        }}
      >
        <Sidebar />

        <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Header />

          <div
            style={{
              flex: 1,
              padding: 'var(--pad) 30px 130px',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--pad)'
            }}
          >
            {app.error && <ErrorBanner message={app.error} />}
            {app.loading ? <Loading /> : <Screen />}
          </div>

          <BulkBar />
          <Toast />
        </main>
      </div>
    </div>
  );
}

function Screen() {
  const { screen } = useApp();
  switch (screen) {
    case 'dash':
      return <Dashboard />;
    case 'fleet':
      return <Fleet />;
    case 'detail':
      return <Detail />;
    case 'commands':
      return <Commands />;
    case 'files':
      return <Files />;
    case 'rdp':
      return <Rdp />;
    case 'net':
      return <Net />;
    case 'tasks':
      return <Tasks />;
    case 'alerts':
      return <Alerts />;
    default:
      return <Dashboard />;
  }
}

function Loading() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '40px 20px',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r)',
        background: 'var(--panel)',
        fontSize: 12.5,
        color: 'var(--ink3)'
      }}
    >
      <span
        style={{
          width: 14,
          height: 14,
          borderRadius: '50%',
          border: '2px solid var(--line2)',
          borderTopColor: 'var(--acc)',
          animation: 'spin .8s linear infinite'
        }}
      />
      Собираю срез парка…
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div
      style={{
        padding: '14px 18px',
        border: '1px solid var(--bad)',
        borderRadius: 'var(--r)',
        background: 'var(--badSoft)',
        fontSize: 12.5,
        color: 'var(--ink)'
      }}
    >
      Сервер недоступен: {message}. Проверьте, что запущен <code>npm start</code>.
    </div>
  );
}

function Splash() {
  return (
    <div
      style={{
        ...themeStyle(storedTheme(), false),
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg)',
        color: 'var(--ink3)',
        fontFamily: 'var(--fs)',
        fontSize: 13
      }}
    >
      Fleet Console…
    </div>
  );
}
